import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getHomeProducedEnergyList,
  addEnergyToBusiness,
  updateEnergy,
  deleteEnergy,
} from "../../../../../../apis/Business/EnergyApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { getGlobalIdematHomeEnergy } from "../../../../../../apis/Dropdown";

const HomeProducedTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const [dropdownUsedFor, setDropdownUsedFor] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  useEffect(() => {
    getGlobalIdematHomeEnergy({ setDropdown: setDropdownUsedFor });
  }, [t]);

  const fetchData = useCallback(() => {
    getHomeProducedEnergyList({
      businessId,
      setObjects: setData,
    });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue = row["type.value"]?.value || row.type?.value;
        if (materialValue) {
          const selectedMaterial = dropdownUsedFor.find(
            (option) => option?.value === materialValue
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownUsedFor]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "type.value": {
        targetField: "qty_consumed",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedMaterial) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit,
              }));
            }
          }
        },
        dropdownDataKey: "type.value",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      let typeObj = {};
      if (editedItem["type.value"]) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === editedItem["type.value"].value
        );
      } else if (editedItem["type"].value) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === editedItem["type"].value
        );
      }

      const typedb =
        typeObj && typeObj.typeDB
          ? typeObj.typeDB
          : editedItem.type && typeof editedItem.type === "string"
          ? editedItem.type
          : data["typedb"];
      updateEnergy({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        category: "home-produced",
        type: editedItem["type.value"]
          ? editedItem["type.value"].value
          : editedItem["type"]
          ? editedItem["type"].value
          : null,
        source: null,
        typedb: typedb,
        qtyConsumed: editedItem["qty_consumed"],
        qtySold: editedItem["qty_sold"],
        qtyWasted: editedItem["qty_wasted"],
        energyWasted: editedItem["energy_wasted"],
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, dropdownUsedFor, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      let typeObj = {};
      if (newItem["type.value"]) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === newItem["type.value"].value
        );
      }
      addEnergyToBusiness({
        businessId,
        ...newItem,
        category: "home-produced",
        type: newItem["type.value"] ? newItem["type.value"].value : null,
        typedb: typeObj ? typeObj.typeDB : newItem.type,
        qtyConsumed: newItem["qty_consumed"],
        qtySold: newItem["qty_sold"],
        qtyWasted: newItem["qty_wasted"],
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, dropdownUsedFor, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteEnergy({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "type.value",
        header: `${t("ENERGY_TYPE")}*`,
        type: "dropdown",
        dropdownData: dropdownUsedFor,
        disableSortBy: true,
      },
      {
        accessor: "qty_consumed",
        header: (props) => {
          const rowId = props.row?.original?.id;
          return `${t("QUANTITY_CONSUMED")}* (${unitMaterials[rowId] || "xx"})`;
        },
        type: "number",
        tipHeader: "Tip",
        tipBody: t("QTY_CONSUMED_TIPBOX"),
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        unit: (rowId, rowData) => {
          const rowType =
            rowData?.type?.value || rowData?.["type.value"]?.value;
          return rowType
            ? dropdownUsedFor.find((option) => option.value === rowType)?.unit
            : unitMaterials[rowId] || "xx";
        },
      },
      {
        accessor: "qty_sold",
        header: t("QUANTITY_SOLD"),
        type: "number",
        tipHeader: "Tip",
        tipBody: t("QTY_SOLD_TIPBOX"),
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
      },
      {
        accessor: "qty_wasted",
        header: t("QUANTITY_WASTED"),
        type: "number",
        tipHeader: "Tip",
        tipBody: t("QTY_WASTED_TIPBOX"),
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "energy_wasted",
        header: t("ENERGY_WASTED"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        subtotalUnit: "tCO₂e",
        width: "120px",
        tipHeader: "Tip",
        tipBody: t("ENERGY_WASTED_TIPBOX"),
        subtotalFixed: true,
        optional: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [dropdownUsedFor, t, unitMaterials]
  );

  const disableRules = {
    energy_wasted: () => true,
    carbon: () => true,
  };

  const subtotalColumns = {
    energy_wasted: { label: t("TOTAL_ENERGY_WASTED") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "type.value": dropdownUsedFor,
    }),
    [dropdownUsedFor]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default HomeProducedTable;
